import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";

// Site::Top::MvArea::Component の Stimulus コントローラ。
// landIeul: components/top/Area-select.tsx の useState(active/areaId/prefectureId) 相当を再現する。
// STEP1 で土地の有無を選択 → STEP2 でエリア→都道府県を選択 → プラン請求スタートで /plan-request/ へ遷移。
interface Prefecture {
  id: string;
  name: string;
  area_id: string;
}

class SiteTopMvAreaController extends Controller {
  static targets = [
    "step2",
    "step2Text",
    "areaGroup",
    "cityGroup",
    "prevButton",
    "startWrapper",
    "startButton",
  ];
  static values = { prefectures: Array, utm: String };

  declare readonly step2Target: HTMLElement;
  declare readonly step2TextTarget: HTMLElement;
  declare readonly areaGroupTarget: HTMLElement;
  declare readonly cityGroupTarget: HTMLElement;
  declare readonly prevButtonTarget: HTMLButtonElement;
  declare readonly startWrapperTarget: HTMLElement;
  declare readonly startButtonTarget: HTMLButtonElement;
  declare readonly prefecturesValue: Prefecture[];
  declare readonly utmValue: string;

  // Area-select.tsx: active(-1 未選択 / 0 はい / 1 いいえ), areaId, prefectureId
  active = -1;
  areaId = "";
  prefectureId = "";

  private get activeClass(): string {
    return "mvArea_active";
  }

  private get activePartsClass(): string {
    return "mvArea_activeParts";
  }

  // STEP1: 土地の有無
  selectAnswer(event: ActionEvent): void {
    this.active = Number(event.params.answer);
    const clicked = event.currentTarget as HTMLElement;
    // クリックしたボタンだけ active に
    clicked.parentElement
      ?.querySelectorAll("button")
      .forEach((b) => b.classList.remove(this.activeClass));
    clicked.classList.add(this.activeClass);
    // STEP2 を活性化・エリアボタンを有効化
    this.step2Target.classList.add(this.activePartsClass);
    this.areaGroupTarget
      .querySelectorAll<HTMLButtonElement>("button")
      .forEach((b) => (b.disabled = false));
    // 「いいえ」を選んだ場合は STEP2 の説明文を差し替える
    this.step2TextTarget.textContent =
      this.active === 1
        ? "土地からお探しの方は、まずはご希望のエリア、または現住所をご入力いただければ、最適なプランをご紹介します。"
        : "活用したい土地のエリアを入力してください";
  }

  // STEP2: エリア選択 → 該当都道府県ボタンを描画
  selectArea(event: ActionEvent): void {
    this.areaId = String(event.params.area);
    const clicked = event.currentTarget as HTMLElement;
    this.areaGroupTarget
      .querySelectorAll("button")
      .forEach((b) => b.classList.remove(this.activeClass));
    clicked.classList.add(this.activeClass);
    // エリア選択後は areaGroup を隠し cityGroup に県ボタンを出す (Area-select.tsx と同じ)
    this.areaGroupTarget.style.display = "none";
    this.renderCityButtons();
    this.prevButtonTarget.disabled = false;
    // エリアを変えたら都道府県はリセット
    this.setPrefecture("");
  }

  // STEP2: 都道府県選択
  selectPrefecture(event: ActionEvent): void {
    const clicked = event.currentTarget as HTMLElement;
    this.cityGroupTarget
      .querySelectorAll("button")
      .forEach((b) => b.classList.remove(this.activeClass));
    clicked.classList.add(this.activeClass);
    this.setPrefecture(String(event.params.pref));
  }

  // エリア選択に戻る
  resetArea(): void {
    this.areaId = "";
    this.setPrefecture("");
    this.areaGroupTarget.style.display = "";
    this.areaGroupTarget
      .querySelectorAll("button")
      .forEach((b) => b.classList.remove(this.activeClass));
    this.cityGroupTarget.innerHTML = "";
    this.prevButtonTarget.disabled = true;
  }

  // プラン請求スタート
  submit(): void {
    const propertyTypeId = this.active === 0 ? "land_usage_yes" : "land_usage_no";
    const query = `&property-type-id=${propertyTypeId}&pref-id=${this.prefectureId}`;
    const href = `/plan-request/?${this.utmValue}${query}`;
    window.open(href, "_self", "noreferrer");
  }

  private renderCityButtons(): void {
    this.cityGroupTarget.innerHTML = "";
    this.prefecturesValue
      .filter((p) => p.area_id === this.areaId)
      .forEach((p) => {
        const btn = document.createElement("button");
        btn.name = "prefecturebutton";
        btn.type = "button";
        btn.value = p.id;
        btn.textContent = p.name;
        btn.setAttribute("data-action", "site--top--mv-area#selectPrefecture");
        btn.setAttribute("data-site--top--mv-area-pref-param", p.id);
        this.cityGroupTarget.appendChild(btn);
      });
  }

  private setPrefecture(id: string): void {
    this.prefectureId = id;
    if (id) {
      this.startWrapperTarget.classList.add("mvArea_activeButton");
      this.startButtonTarget.disabled = false;
    } else {
      this.startWrapperTarget.classList.remove("mvArea_activeButton");
      this.startButtonTarget.disabled = true;
    }
  }
}

interface ActionEvent extends Event {
  params: { answer?: string; area?: string; pref?: string };
  currentTarget: EventTarget & HTMLElement;
}

application.register("site--top--mv-area", SiteTopMvAreaController);
